import { useCallback, useMemo, useState } from 'react'
import { useApp } from '../store/AppContext'
import { useDismiss } from '../lib/hooks'
import * as db from '../lib/db'
import { liveOfKind, SWATCHES, type ClientKind, type ID } from '../lib/types'

type RowKind = 'none' | 'client' | 'project' | 'task'

interface Row {
  key: string
  kind: RowKind
  projectId: ID | null
  task: string
  label: string
  meta: string
  color: string
  indent: boolean
  /** The client or prospect this row sits under, for folding and deleting. */
  groupId?: ID | null
  /** How many projects a heading holds, shown as the count beside its name. */
  count?: number
}

export function ProjectPicker({
  kind,
  projectId,
  task,
  onPick,
  align = 'right',
  clientId = null,
}: {
  /**
   * Which population this picker draws from.
   *
   * Required, and with no default, because a picker that quietly showed both
   * is exactly the crossover this is meant to prevent. A project belongs to
   * one client, so the client's kind decides the project's.
   */
  kind: ClientKind
  projectId: ID | null
  task: string
  onPick: (projectId: ID | null, task: string) => void
  align?: 'left' | 'right'
  /** Narrow the list to one client, set by the ClientPicker beside this one. */
  clientId?: ID | null
}) {
  const { ws, proj, client, act, flash, confirm, canSeeAll } = useApp()
  const [form, setForm] = useState<{ name: string; clientId: string; color: string } | null>(null)
  const [newClient, setNewClient] = useState('')
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const [creating, setCreating] = useState(false)

  /*
   * Folded headings, by client id.
   *
   * Everything starts open, so the list reads exactly as it did for anyone
   * who never touches this. Folding is for the person with twenty clients
   * who wants the one they are looking at on screen at once.
   */
  const [folded, setFolded] = useState<ReadonlySet<string>>(() => new Set())

  const toggleFold = (id: string) =>
    setFolded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const close = useCallback(() => setOpen(false), [])
  const ref = useDismiss<HTMLDivElement>(open, close)

  const selected = proj(projectId)

  const noun = kind === 'prospect' ? 'prospect' : 'client'
  const Noun = kind === 'prospect' ? 'Prospect' : 'Client'

  /**
   * The ids on this side of the line.
   *
   * Projects with no client at all belong to neither kind, and they stay on
   * the client side: that is where unassigned work has always appeared, and
   * moving it would hide time rather than separate it. Nothing prospect-owned
   * reaches the client side, and nothing else reaches the prospect side.
   */
  const inScope = useMemo(() => {
    // Live only: a binned client's projects are not offered for new time.
    const ids = new Set(liveOfKind(ws?.clients ?? [], kind).map((c) => c.id))
    return (clientId: ID | null) =>
      clientId === null ? kind === 'client' : ids.has(clientId)
  }, [ws, kind])

  /**
   * Rows grouped under the client that owns them.
   *
   * A client has many projects, and a flat list with the client as trailing
   * small print made that impossible to see. The client is a heading now and
   * its projects sit beneath it, so picking "the Acme one" means looking in
   * one place rather than scanning every line.
   *
   * Headings are not selectable -- you track time against a project, never a
   * client -- so they render as plain rows and are skipped when picking.
   */
  const rows = useMemo<Row[]>(() => {
    if (!ws) return []
    const query = q.trim().toLowerCase()

    const out: Row[] = []

    /*
     * "No project" is offered on the client side only.
     *
     * An entry with no project has no client, so it has no kind -- choosing
     * it here would file prospect work on the Timer screen and lose it from
     * this one. On the prospect side a project is the only way in.
     */
    if (kind === 'client') {
      out.push({
        key: 'none',
        kind: 'none',
        projectId: null,
        task: '',
        label: 'No project',
        meta: '',
        color: '#CFCABC',
        indent: false,
      })
    }

    // Bucket the matching projects by client first, so a heading is only
    // emitted for a client that still has something to show.
    const buckets = new Map<string, { name: string; projects: typeof ws.projects }>()

    for (const p of ws.projects) {
      if (p.status !== 'active') continue
      if (!inScope(p.clientId)) continue
      // The client filter is a hard narrowing, not a ranking: with one
      // chosen, a project belonging to anybody else is not an option here.
      if (clientId && p.clientId !== clientId) continue
      const c = client(p.clientId)
      const clientName = c?.name ?? ''
      const hit =
        !query ||
        p.name.toLowerCase().includes(query) ||
        clientName.toLowerCase().includes(query) ||
        p.tasks.some((t) => t.toLowerCase().includes(query))
      if (!hit) continue

      const key = c?.id ?? '~none'
      const bucket = buckets.get(key) ?? { name: c?.name ?? 'No client', projects: [] }
      bucket.projects.push(p)
      buckets.set(key, bucket)
    }

    const ordered = [...buckets.entries()]
      .map(([id, v]) => ({ id, ...v }))
      .sort((a, b) => {
        // Projects with no client go last rather than sorting into the Ns.
        if (a.id === '~none') return 1
        if (b.id === '~none') return -1
        return a.name.localeCompare(b.name)
      })

    for (const group of ordered) {
      out.push({
        key: 'client:' + group.id,
        kind: 'client',
        projectId: null,
        task: '',
        label: group.name,
        // The count stands alone now -- the row carries a dot and a fold
        // arrow, so "3" beside the name reads as "three of them" without
        // the word, the way a tree always has.
        meta: '',
        count: group.projects.length,
        groupId: group.id === '~none' ? null : group.id,
        // Borrowed from the first project under it, so a heading is findable
        // by colour the way its children are. Clients carry none of their own.
        color: group.projects[0]?.color ?? 'var(--line)',
        indent: false,
      })

      // Folded away: the heading stays, everything under it goes.
      if (folded.has(group.id)) continue

      for (const p of group.projects) {
        out.push({
          key: p.id,
          kind: 'project',
          projectId: p.id,
          task: '',
          label: p.name,
          meta: '',
          count: p.tasks.length,
          groupId: group.id === '~none' ? null : group.id,
          color: p.color,
          indent: true,
        })

        // Tasks appear once you search, or always for the chosen project.
        const showTasks = query
          ? p.tasks.filter((t) => t.toLowerCase().includes(query))
          : p.id === projectId
            ? p.tasks
            : []

        for (const t of showTasks) {
          out.push({
            key: p.id + ':' + t,
            kind: 'task',
            projectId: p.id,
            task: t,
            label: t,
            meta: p.name,
            color: p.color,
            indent: true,
          })
        }
      }
    }

    return out
  }, [ws, q, client, projectId, clientId, inScope, kind, folded])

  const typed = q.trim()

  // Offer to create only when the name is genuinely new. Matching against
  // every project, not just the filtered rows, so an archived or
  // non-matching duplicate is not silently created twice.
  const nameIsNew =
    typed.length > 0 && !ws?.projects.some((p) => p.name.toLowerCase() === typed.toLowerCase())

  // Shown whether or not anything has been typed: a create action that only
  // appears once you have guessed at it is one nobody finds.
  const mayCreate = Boolean(ws)

  /**
   * Create the project and select it in one step, which is the whole point
   * of being here rather than on the Projects screen. The new project is
   * found by name afterwards because the snapshot is what comes back, not
   * the inserted row.
   */
  /**
   * Create the project, with its client and colour, and select it.
   *
   * db.createProject returns the new id, so there is no waiting for a
   * snapshot and no looking the project back up by name -- which was the
   * fragile part of the first version of this.
   */
  const createAndPick = async () => {
    if (!form || !ws || creating) return
    const name = form.name.trim()
    if (!name) return

    setCreating(true)
    try {
      let clientId: string | null = form.clientId || null

      if (form.clientId === '__new') {
        const named = newClient.trim()
        if (!named) {
          flash(`Give the ${noun} a name, or choose No ${noun}.`)
          return
        }
        clientId = await db.createClient(ws.id, {
          name: named,
          contact: '',
          isProspect: kind === 'prospect',
        })
      }

      if (kind === 'prospect' && !clientId) {
        flash('Choose a prospect for this project.')
        return
      }

      const projectId = await db.createProject(ws.id, {
        name,
        clientId,
        color: form.color,
      })

      // Pull the new rows into the snapshot the screens render from.
      await act.updateWorkspace({})

      onPick(projectId, '')
      flash(`Created ${name}`)
      setForm(null)
      setNewClient('')
      setOpen(false)
      setQ('')
    } catch (err) {
      flash(err instanceof Error ? err.message : 'Could not create the project.')
    } finally {
      setCreating(false)
    }
  }

  /*
   * Deleting from inside a picker.
   *
   * A control people open to CHOOSE something now also removes things, which
   * is only safe if the two cannot be confused. So the X sits at the far
   * right, outside the row's own click target, and every one of them opens
   * the same confirmation the management screens use -- nothing goes on a
   * single click here.
   *
   * Hidden from members: both deletes need admin rights, so for everyone
   * else the button could only ever fail.
   */
  const binGroup = (groupId: ID, name: string) =>
    confirm({
      title: `Delete ${name}?`,
      body:
        `Its projects will stop rolling up under it, and time already logged ` +
        `stays exactly where it is. You can put it back from the ${noun === 'prospect' ? 'Prospects' : 'Clients'} screen.`,
      cta: `Delete ${noun}`,
      danger: true,
      onConfirm: () => void act.binClient(groupId),
    })

  const removeProject = (p: { id: ID; name: string }) =>
    confirm({
      title: `Delete ${p.name}?`,
      body:
        'Entries logged against it are kept but become "No project", so they ' +
        'stop counting towards amounts. A project has no bin: this cannot be ' +
        'undone.',
      cta: 'Delete project',
      danger: true,
      requireText: p.name,
      onConfirm: () => void act.deleteProject(p.id),
    })

  const label = selected ? (task ? `${selected.name} · ${task}` : selected.name) : 'Project'

  return (
    <div ref={ref} style={{ position: 'relative', flexShrink: 0 }}>
      <button
        type="button"
        className={selected ? 'picker-btn picker-btn--set' : 'picker-btn'}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => {
          setOpen((v) => !v)
          setQ('')
        }}
      >
        <span className="dot" style={{ background: selected?.color ?? 'var(--line)' }} aria-hidden />
        <span className="trunc" title={label}>
          {label}
        </span>
      </button>

      {open && (
        <div className="popover popover--down" style={{ [align]: 0 } as React.CSSProperties} role="listbox">
          <input
            autoFocus
            className="popover-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search projects or clients…"
            aria-label="Search projects or clients"
          />
          <div style={{ padding: 6 }}>
            {rows.map((r) =>
              // A client heading is a label, not a choice: you track time
              // against a project. Rendering it as a div keeps it out of the
              // keyboard order and off the list of options.
              r.kind === 'client' ? (
                <div
                  key={r.key}
                  role="presentation"
                  style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 4px 2px' }}
                >
                  <button
                    type="button"
                    className="tree-twisty"
                    aria-expanded={!folded.has(r.groupId ?? '~none')}
                    aria-label={`${folded.has(r.groupId ?? '~none') ? 'Show' : 'Hide'} ${r.label}`}
                    onClick={() => toggleFold(r.groupId ?? '~none')}
                  >
                    <span
                      aria-hidden
                      style={{
                        display: 'inline-block',
                        transform: folded.has(r.groupId ?? '~none') ? 'rotate(-90deg)' : 'none',
                        transition: 'transform .12s',
                      }}
                    >
                      ▾
                    </span>
                  </button>
                  <span className="dot" style={{ background: r.color }} aria-hidden />
                  <span
                    className="trunc eyebrow"
                    style={{ flex: 1, minWidth: 0, color: 'var(--mut)' }}
                    title={r.label}
                  >
                    {r.label}
                  </span>
                  <span className="tree-count">{r.count}</span>
                  {canSeeAll && r.groupId && (
                    <button
                      type="button"
                      className="ibtn ibtn--del tree-del"
                      title={`Delete ${r.label}`}
                      aria-label={`Delete ${r.label}`}
                      onClick={() => binGroup(r.groupId as ID, r.label)}
                    >
                      ✕
                    </button>
                  )}
                </div>
              ) : (
                <div key={r.key} className="tree-line">
                  <button
                    type="button"
                    role="option"
                    aria-selected={r.projectId === projectId && r.task === task}
                    className="popover-row"
                    style={{
                      flex: 1,
                      minWidth: 0,
                      paddingLeft: r.indent ? (r.kind === 'task' ? 44 : 28) : undefined,
                    }}
                    onClick={() => {
                      onPick(r.projectId, r.task)
                      setOpen(false)
                    }}
                  >
                    <span className="dot" style={{ background: r.color }} aria-hidden />
                    <span className="trunc" style={{ flex: 1, minWidth: 0 }} title={r.label}>
                      {r.label}
                    </span>
                    {r.meta && (
                      <span style={{ fontSize: 11.5, color: 'var(--faint)' }}>{r.meta}</span>
                    )}
                    {/* Tasks, counted the way the heading counts projects. */}
                    {r.kind === 'project' && r.count ? (
                      <span className="tree-count">{r.count}</span>
                    ) : null}
                  </button>

                  {canSeeAll && r.kind === 'project' && r.projectId && (
                    <button
                      type="button"
                      className="ibtn ibtn--del tree-del"
                      title={`Delete ${r.label}`}
                      aria-label={`Delete ${r.label}`}
                      onClick={() => removeProject({ id: r.projectId as ID, name: r.label })}
                    >
                      ✕
                    </button>
                  )}
                </div>
              ),
            )}
            {mayCreate && !form && (
              <button
                type="button"
                className="popover-row popover-create"
                title={nameIsNew ? `Create “${typed}”` : 'Create a project'}
                style={{
                  borderTop: '1px solid var(--line)',
                  marginTop: 4,
                  paddingTop: 9,
                }}
                onClick={() =>
                  // Only carry the typed text across when it would be a new
                  // name; pre-filling an existing one invites a duplicate.
                  setForm({
                    name: nameIsNew ? typed : '',
                    // With nothing narrowed, the prospect side opens on "new"
                    // rather than on an option it does not offer.
                    clientId: clientId ?? (kind === 'prospect' ? '__new' : ''),
                    color: SWATCHES[0],
                  })
                }
              >
                <span aria-hidden style={{ width: 9, textAlign: 'center', color: 'var(--ac)', fontWeight: 700 }}>
                  +
                </span>
                <span className="trunc" style={{ flex: 1, minWidth: 0 }}>
                  {nameIsNew ? `Create project “${typed}”` : 'New project'}
                </span>
              </button>
            )}

            {form && (
              <div
                style={{
                  borderTop: '1px solid var(--line)',
                  marginTop: 4,
                  paddingTop: 10,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 9,
                }}
              >
                <input
                  autoFocus
                  className="input"
                  placeholder="Project name"
                  aria-label="Project name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') void createAndPick()
                    if (e.key === 'Escape') setForm(null)
                  }}
                />

                <select
                  className="role-select"
                  aria-label={Noun}
                  value={form.clientId}
                  onChange={(e) => setForm({ ...form, clientId: e.target.value })}
                >
                  {/* A prospect's project must belong to a prospect: "no
                      client" on this side would make a project that then
                      shows up on the Timer screen instead. */}
                  {kind === 'client' && <option value="">No client</option>}
                  {liveOfKind(ws?.clients ?? [], kind)
                    .filter((c) => c.status === 'active')
                    .map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  <option value="__new">+ New {noun}…</option>
                </select>

                {form.clientId === '__new' && (
                  <input
                    className="input"
                    placeholder={`New ${noun} name`}
                    aria-label={`New ${noun} name`}
                    value={newClient}
                    onChange={(e) => setNewClient(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') void createAndPick()
                      if (e.key === 'Escape') setForm(null)
                    }}
                  />
                )}

                <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 12, color: 'var(--mut)', marginRight: 2 }}>Colour</span>
                  {SWATCHES.map((c) => (
                    <button
                      key={c}
                      type="button"
                      aria-label={`Colour ${c}`}
                      aria-pressed={form.color === c}
                      onClick={() => setForm({ ...form, color: c })}
                      style={{
                        width: 20,
                        height: 20,
                        borderRadius: '50%',
                        background: c,
                        cursor: 'pointer',
                        // The ring is the only selected-state cue, so it has
                        // to be visible against every swatch.
                        border: form.color === c ? '2px solid var(--ink)' : '2px solid transparent',
                        boxShadow: form.color === c ? '0 0 0 2px var(--canvas) inset' : 'none',
                      }}
                    />
                  ))}
                </div>

                <div className="row" style={{ gap: 8 }}>
                  <button
                    type="button"
                    className="btn btn--primary"
                    disabled={creating || !form.name.trim()}
                    onClick={() => void createAndPick()}
                  >
                    {creating ? 'Creating…' : 'Create'}
                  </button>
                  <button
                    type="button"
                    className="btn"
                    onClick={() => {
                      setForm(null)
                      setNewClient('')
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {rows.length === 1 && !typed && (
              <div style={{ padding: '10px 10px 6px', fontSize: 12.5, color: 'var(--mut)' }}>
                No active projects yet.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
